import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { isExpenseDatePreset } from '@/api/expenses';
import type { AllExpensesFilters } from '@/api/expenses';

type SortBy = AllExpensesFilters['sortBy'];
type SortOrder = AllExpensesFilters['sortOrder'];

function parseFiltersFromParams(params: URLSearchParams): AllExpensesFilters {
  const filters: AllExpensesFilters = {};

  const search = params.get('search');
  if (search) filters.search = search;

  const groupIds = params.getAll('group');
  if (groupIds.length > 0) filters.groupIds = groupIds;

  const categoryIds = params.getAll('category');
  if (categoryIds.length > 0) filters.categoryIds = categoryIds;

  const paidBy = params.get('paid_by');
  if (paidBy) filters.paidBy = paidBy;

  const datePreset = params.get('date');
  if (isExpenseDatePreset(datePreset)) filters.datePreset = datePreset;

  const startDate = params.get('from');
  if (startDate) filters.startDate = startDate;
  const endDate = params.get('to');
  if (endDate) filters.endDate = endDate;

  const minAmount = params.get('min');
  if (minAmount) filters.minAmount = Number(minAmount);
  const maxAmount = params.get('max');
  if (maxAmount) filters.maxAmount = Number(maxAmount);

  if (params.get('mine') === '1') filters.myExpensesOnly = true;

  const sortBy = params.get('sort') as SortBy;
  if (sortBy) filters.sortBy = sortBy;

  const sortOrder = params.get('order') as SortOrder;
  if (sortOrder) filters.sortOrder = sortOrder;

  return filters;
}

function filtersToParams(filters: AllExpensesFilters): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.search) params.set('search', filters.search);

  for (const id of filters.groupIds ?? []) {
    params.append('group', id);
  }
  for (const id of filters.categoryIds ?? []) {
    params.append('category', id);
  }

  if (filters.paidBy) params.set('paid_by', filters.paidBy);
  if (filters.datePreset && filters.datePreset !== 'all') params.set('date', filters.datePreset);
  if (filters.startDate) params.set('from', filters.startDate);
  if (filters.endDate) params.set('to', filters.endDate);
  if (filters.minAmount !== undefined) params.set('min', String(filters.minAmount));
  if (filters.maxAmount !== undefined) params.set('max', String(filters.maxAmount));
  if (filters.myExpensesOnly) params.set('mine', '1');
  if (filters.sortBy && filters.sortBy !== 'date') params.set('sort', filters.sortBy);
  if (filters.sortOrder && filters.sortOrder !== 'desc') params.set('order', filters.sortOrder);

  return params;
}

export function useExpenseFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => parseFiltersFromParams(searchParams), [searchParams]);

  const setFilters = useCallback(
    (updates: Partial<AllExpensesFilters>) => {
      const merged = { ...filters, ...updates };
      setSearchParams(filtersToParams(merged), { replace: true });
    },
    [filters, setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  const hasActiveFilters = useMemo(() => {
    const { sortBy, sortOrder, ...rest } = filters;
    return Object.values(rest).some(v =>
      v !== undefined && v !== false && !(Array.isArray(v) && v.length === 0),
    );
  }, [filters]);

  return { filters, setFilters, clearFilters, hasActiveFilters };
}
